import type { AxiosResponse } from 'axios';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';
import axiosInstance, { getErrorMessage } from '../axios.config';

const apiLogger = createLogger(LOG_NAMESPACES.API);

export interface CreateSpace {
  name: string;
  description: string;
  color: string;
  logo?: string;
  clearance_level: string;
  invoker_id: string;
}

export interface UpdateSpace extends CreateSpace {
  id: string;
}

export interface UserInvite {
  space_id: string;
  expiration: string;
  email: string;
  roles: string[];
  clearance: string;
}

export interface SpaceUser {
  doc_type: 'user_space' | 'user_invite_space';
  id: string;
  space_id: string;
  email: string;
  roles: string[];
  clearance: string;
  last_tx_time: string;
  creation_date?: string;
  update_date?: string;
  is_invite: boolean;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email_admin?: string;
  user_exists?: boolean;
  status?: 'pending' | 'accepted' | 'rejected';
}

interface GroupData {
  name: string;
  space_id: string;
  color: string;
  users: string[];
}

interface UpdateUserData {
  newClearance?: string;
  roles?: string[];
  spaceId: string;
  userId: string;
}

interface RemoveUserData {
  userId: string;
  spaceId: string;
}

interface UpdateGroupData {
  color: string;
  groupId: string;
  name: string;
  spaceId?: string;
}

interface CancelInvitationData {
  email?: string;
  spaceId: string;
}

// SPACE API FUNCTIONS
export const createSpace = async (body: CreateSpace): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Creating space', { body });
    const response = await axiosInstance.post('chaincode/space', body, {
      withCredentials: true,
    });
    apiLogger.info('Space created successfully', { id: response.data.id });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to create space', { error: errorMessage, body });
    throw new Error(errorMessage);
  }
};

export const getAllSpaces = async (): Promise<any[]> => {
  try {
    apiLogger.info('Fetching all spaces');
    const response = await axiosInstance.get('chaincode/space/all', {
      withCredentials: true,
    });
    apiLogger.info('Fetched all spaces', { count: response.data.result?.length });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch all spaces', { error: errorMessage });
    throw new Error(errorMessage);
  }
};

export const getSpaceById = async (id?: string): Promise<any> => {
  try {
    if (!id) {
      throw new Error('Space ID is required');
    }
    apiLogger.info('Fetching space by ID', { id });
    const response = await axiosInstance.get(`chaincode/space/${id}`, {
      withCredentials: true,
    });
    apiLogger.info('Fetched space by ID', { id });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch space by ID', { error: errorMessage, id });
    throw new Error(errorMessage);
  }
};

export const updateSpace = async (body: UpdateSpace): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Updating space', { id: body.id });
    const response = await axiosInstance.patch(`chaincode/space/${body.id}`, body, {
      withCredentials: true,
    });
    apiLogger.info('Space updated successfully', { id: body.id });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to update space', { error: errorMessage, id: body.id });
    throw new Error(errorMessage);
  }
};

export const deleteSpace = async (id: string): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Deleting space', { id });
    const response = await axiosInstance.delete(`chaincode/space/${id}`, {
      withCredentials: true,
    });
    apiLogger.info('Space deleted successfully', { id });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to delete space', { error: errorMessage, id });
    throw new Error(errorMessage);
  }
};

export const inviteUser = async (body: UserInvite): Promise<string> => {
  try {
    apiLogger.info('Inviting user to space', { email: body.email, spaceId: body.space_id });
    const response = await axiosInstance.post('chaincode/space/invite', body, {
      withCredentials: true,
    });
    const result = `${body.email} ${response.data.result}`;
    apiLogger.info('User invited successfully', { result });
    return result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to invite user', { error: errorMessage, email: body.email });
    throw new Error(errorMessage);
  }
};

export const fetchSpaceUsers = async (spaceId: string | undefined): Promise<SpaceUser[]> => {
  try {
    if (!spaceId) {
      throw new Error('Space ID is required');
    }
    apiLogger.info('Fetching space users', { spaceId });
    const response = await axiosInstance.get(`chaincode/space/${spaceId}/users`, {
      withCredentials: true,
    });

    const result = (response.data.result as SpaceUser[]).sort((a: SpaceUser, b: SpaceUser) => {
      if (a.is_invite !== b.is_invite) {
        return a.is_invite ? 1 : -1;
      }
      return new Date(a.last_tx_time).getTime() - new Date(b.last_tx_time).getTime();
    });

    apiLogger.info('Fetched space users', { spaceId, count: result.length });
    return result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch space users', { error: errorMessage, spaceId });
    throw new Error(errorMessage);
  }
};

export const getAllNotifications = async (): Promise<any[]> => {
  try {
    apiLogger.info('Fetching all notifications');
    const response = await axiosInstance.get('chaincode/space/invite', {
      withCredentials: true,
    });
    apiLogger.info('Fetched all notifications', { count: response.data.result?.length });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch all notifications', { error: errorMessage });
    throw new Error(errorMessage);
  }
};

export const acceptInvite = async (spaceId: string): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Accepting invitation', { spaceId });
    const response = await axiosInstance.patch(`chaincode/space/invite/${spaceId}/accepted`, {});
    apiLogger.info('Invitation accepted successfully', { spaceId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to accept invitation', { error: errorMessage, spaceId });
    throw new Error(errorMessage);
  }
};

export const updateSpaceUser = async (body: UpdateUserData): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Updating space user', { userId: body.userId, spaceId: body.spaceId });
    const response = await axiosInstance.patch(`chaincode/space/${body.spaceId}/user/${body.userId}`, {
      new_clearance: body.newClearance,
      roles: body.roles,
      space_id: body.spaceId,
      user_id: body.userId,
    });
    apiLogger.info('Space user updated successfully', { userId: body.userId, spaceId: body.spaceId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to update space user', { error: errorMessage, userId: body.userId, spaceId: body.spaceId });
    throw new Error(errorMessage);
  }
};

export const removeSpaceUser = async (body: RemoveUserData): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Removing space user', { userId: body.userId, spaceId: body.spaceId });
    const response = await axiosInstance.delete(`chaincode/space/${body.spaceId}/user/${body.userId}`, {
      withCredentials: true,
    });
    apiLogger.info('Space user removed successfully', { userId: body.userId, spaceId: body.spaceId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to remove space user', { error: errorMessage, userId: body.userId, spaceId: body.spaceId });
    throw new Error(errorMessage);
  }
};

export const createGroup = async (body: GroupData): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Creating group', { name: body.name, spaceId: body.space_id });
    const response = await axiosInstance.post('chaincode/group', body, {
      withCredentials: true,
    });
    apiLogger.info('Group created successfully', { name: body.name, spaceId: body.space_id });
    return response;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to create group', { error: errorMessage, name: body.name });
    throw new Error(errorMessage);
  }
};

export const removeUserFromGroup = async (body: { groupId: string; userId: string }): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Removing user from group', { userId: body.userId, groupId: body.groupId });
    const response = await axiosInstance.delete(`chaincode/group/${body.groupId}/user/${body.userId}`, {
      withCredentials: true,
    });
    apiLogger.info('User removed from group successfully', { userId: body.userId, groupId: body.groupId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to remove user from group', { error: errorMessage, userId: body.userId, groupId: body.groupId });
    throw new Error(errorMessage);
  }
};

export const getGroupUsers = async (groupId: string): Promise<any[]> => {
  try {
    apiLogger.info('Fetching group users', { groupId });
    const response = await axiosInstance.get(`chaincode/group/users/${groupId}`, {
      withCredentials: true,
    });
    apiLogger.info('Fetched group users', { groupId, count: response.data.result?.length });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch group users', { error: errorMessage, groupId });
    throw new Error(errorMessage);
  }
};

export const addGroupUsers = async (body: { groupId?: string; usersjson: any }): Promise<any[]> => {
  try {
    apiLogger.info('Adding users to group', { groupId: body.groupId });
    const response = await axiosInstance.post('chaincode/group/users', {
      group_id: body.groupId,
      usersjson: body.usersjson,
    });
    apiLogger.info('Users added to group successfully', { groupId: body.groupId });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to add users to group', { error: errorMessage, groupId: body.groupId });
    throw new Error(errorMessage);
  }
};

export const getGroupData = async (token: string, body: { groupId: string }): Promise<any> => {
  try {
    apiLogger.info('Fetching group data', { groupId: body.groupId });
    const response = await axiosInstance.get(`chaincode/group/${body.groupId}`, {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    apiLogger.info('Fetched group data', { groupId: body.groupId });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch group data', { error: errorMessage, groupId: body.groupId });
    throw new Error(errorMessage);
  }
};

export const getSpaceGroups = async (spaceId: string | undefined): Promise<any[]> => {
  try {
    if (!spaceId) {
      throw new Error('Space ID is required');
    }
    apiLogger.info('Fetching space groups', { spaceId });
    const response = await axiosInstance.get(`chaincode/group/all/${spaceId}`, {
      withCredentials: true,
    });
    apiLogger.info('Fetched space groups', { spaceId, count: response.data.result?.length });
    return response.data.result;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to fetch space groups', { error: errorMessage, spaceId });
    throw new Error(errorMessage);
  }
};

export const updateGroup = async (body: UpdateGroupData): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Updating group', { groupId: body.groupId, name: body.name });
    const response = await axiosInstance.patch(`chaincode/group/${body.groupId}`, {
      color: body.color,
      groupId: body.groupId,
      name: body.name,
      space_id: body.spaceId,
    });
    apiLogger.info('Group updated successfully', { groupId: body.groupId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to update group', { error: errorMessage, groupId: body.groupId });
    throw new Error(errorMessage);
  }
};

export const deleteGroup = async (groupId: string): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Deleting group', { groupId });
    const response = await axiosInstance.delete(`chaincode/group/${groupId}`, {
      withCredentials: true,
    });
    apiLogger.info('Group deleted successfully', { groupId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to delete group', { error: errorMessage, groupId });
    throw new Error(errorMessage);
  }
};

export const deleteGroupUser = async (params: { groupId?: string; userId?: string }): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Deleting group user', { userId: params.userId, groupId: params.groupId });
    const response = await axiosInstance.delete(`chaincode/group/${params.groupId}/user/${params.userId}`, {
      withCredentials: true,
    });
    apiLogger.info('Group user deleted successfully', { userId: params.userId, groupId: params.groupId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to delete group user', { error: errorMessage, userId: params.userId, groupId: params.groupId });
    throw new Error(errorMessage);
  }
};

export const cancelInvitation = async (body: CancelInvitationData): Promise<AxiosResponse> => {
  try {
    apiLogger.info('Canceling invitation', { email: body.email, spaceId: body.spaceId });
    const response = await axiosInstance.delete(`chaincode/space/invite/${body.spaceId}`, {
      data: {
        email: body.email,
      },
      withCredentials: true,
    });
    apiLogger.info('Invitation canceled successfully', { email: body.email, spaceId: body.spaceId });
    return response.data;
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    apiLogger.error('Failed to cancel invitation', { error: errorMessage, email: body.email, spaceId: body.spaceId });
    throw new Error(errorMessage);
  }
};
